import type {
    ActivateOffer,
    ActivateOfferResponse,
    LoyaltyMemberPoints,
    LoyaltyOffersResponse,
    PointsModel,
} from '@libs/common-types';
import type { LoyaltyPurchasePoints } from '@libs/common-node';
import { AUTH_HEADERS, type Nullable } from '@libs/core';
import { CWEA_ENDPOINTS } from '../constants';
import { getAccessToken } from '../utils';
import { cweaClient } from '../clients/api-proxy-browser-client';

export async function getAllLoyaltyData() {
    const [memberPoints, offers, pointsHistory] = await Promise.all([
        getMemberPoints(),
        getOffers(),
        getPointsHistory(),
    ]);

    return {
        memberPoints,
        offers,
        pointsHistory,
    };
}

export async function activateOffer(offer: ActivateOffer) {
    const token = getAccessToken();

    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.activateOffer;
    return await cweaClient.post<
        ActivateOfferResponse,
        Omit<ActivateOffer, 'activationPromoId'> & {
            sourceId: '2801';
            activationPromoId: string;
        }
    >(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
        body: {
            activationPromoId: offer.activationPromoId.toString(),
            intType: offer.intType,
            memberDescriptor: offer.memberDescriptor,
            memberGroup: offer.memberGroup,
            offerId: offer.offerId,
            // [AC-1618]: will be controlled by BFF, can be removed once AC-1618 is complete
            sourceId: '2801',
            transactionType: offer.transactionType,
            version: offer.version,
        },
    });
}

// TODO: can be deleted once removed from userSessionWrapper
export async function getLoyaltyPoints(token: string | undefined): Promise<Nullable<LoyaltyPurchasePoints>> {
    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.getLoyaltyPoints;
    const response = await cweaClient.get<LoyaltyPurchasePoints[]>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
        useOldFetch: true,
    });

    if (!Array.isArray(response)) {
        return null;
    }

    return response.find(points => points.type === 'Purchase_Points');
}

export async function getMemberPoints() {
    const token = getAccessToken();

    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.getMemberPoints;
    return await cweaClient.get<LoyaltyMemberPoints>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
    });
}

export async function getOffers() {
    const token = getAccessToken();

    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.getOffers;
    return await cweaClient.get<LoyaltyOffersResponse>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
    });
}

export async function getPointsHistory() {
    const token = getAccessToken();

    if (!token) {
        return null;
    }

    const url = CWEA_ENDPOINTS.loyalty.getPointsHistory;
    return await cweaClient.get<PointsModel[]>(url, {
        headers: {
            [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${token}`,
        },
    });
}
